import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";

import { NotificationToast } from "../../../../../components/Generic/Toast/NotificationToast";
import { Despatch } from "../../../../../models/despatch";
import RoutePlannerTaskService from "../../../../../services/route.planner/task.service";
import { DespatchKeys, RoutePlannerKeys } from "../../../../../util/queries";

const useRoutePlannerAction = () => {
  const { t: translate } = useTranslation();
  const queryClient = useQueryClient();

  const useAddToRoutePlanner = () => {
    return useMutation({
      mutationKey: RoutePlannerKeys.createTask(),
      mutationFn: (despatch: Despatch) => RoutePlannerTaskService.create(despatch),
      onMutate: () => {
        NotificationToast.info(translate("addingToRoutePlanner"));
      },
      onSuccess: () => {
        NotificationToast.success(translate("addedToRoutePlanner"));
        queryClient.invalidateQueries({ queryKey: [DespatchKeys.all] });
      },
      onError: (error: AxiosError<Error>) => {
        if (error && error.response) NotificationToast.error(error?.response?.data?.message);
        else NotificationToast.error(translate("couldNotAddToRoutePlanner"));
      },
    });
  };

  const useAddListToRoutePlanner = () => {
    return useMutation({
      mutationKey: RoutePlannerKeys.createTaskList(),
      mutationFn: (despatches: Despatch[]) => RoutePlannerTaskService.createList(despatches),
      onMutate: () => {
        NotificationToast.info(translate("addingToRoutePlanner"));
      },
      onSuccess: () => {
        NotificationToast.success(translate("addedToRoutePlanner"));
        queryClient.invalidateQueries({ queryKey: [DespatchKeys.all] });
      },
      onError: (error: AxiosError<Error>) => {
        if (error && error.response) NotificationToast.error(error?.response?.data?.message);
        else NotificationToast.error(translate("couldNotAddToRoutePlanner"));
      },
    });
  };

  const useUpdateToRoutePlanner = () => {
    return useMutation({
      mutationKey: RoutePlannerKeys.updateTask(),
      mutationFn: ({ id, despatch }: { id: string; despatch: Despatch }) =>
        RoutePlannerTaskService.update(id, despatch),
      onMutate: () => {
        NotificationToast.info(translate("updatingInRoutePlanner"));
      },
      onSuccess: () => {
        NotificationToast.success(translate("updatedInRoutePlanner"));
      },
      onError: (error: AxiosError<Error>) => {
        if (error && error.response) NotificationToast.error(error?.response?.data?.message);
        else NotificationToast.error(translate("couldNotUpdateInRoutePlanner"));
      },
    });
  };

  const useRemoveListFromRoutePlanner = () => {
    return useMutation({
      mutationKey: RoutePlannerKeys.deleteTaskList(),
      mutationFn: (despatches: Despatch[]) => RoutePlannerTaskService.deleteList(despatches),
      onMutate: () => {
        NotificationToast.info(translate("removingFromRoutePlanner"));
      },
      onSuccess: () => {
        NotificationToast.success(translate("removedFromRoutePlanner"));
        queryClient.invalidateQueries({ queryKey: [DespatchKeys.all] });
      },
      onError: (error: AxiosError<Error>) => {
        if (error && error.response) NotificationToast.error(error?.response?.data?.message);
        else NotificationToast.error(translate("couldNotRemoveFromRoutePlanner"));
      },
    });
  };

  const useRemoveFromRoutePlanner = () => {
    return useMutation({
      mutationKey: RoutePlannerKeys.deleteTask(),
      mutationFn: (id: number) => RoutePlannerTaskService.delete(id),
      onMutate: () => {
        NotificationToast.info(translate("removingFromRoutePlanner"));
      },
      onSuccess: () => {
        NotificationToast.success(translate("removedFromRoutePlanner"));
        queryClient.invalidateQueries({ queryKey: [DespatchKeys.all] });
      },
      onError: (error: AxiosError<Error>) => {
        if (error && error.response) NotificationToast.error(error?.response?.data?.message);
        else NotificationToast.error(translate("couldNotRemoveFromRoutePlanner"));
      },
    });
  };

  return {
    useAddToRoutePlanner,
    useRemoveFromRoutePlanner,
    useUpdateToRoutePlanner,
    useAddListToRoutePlanner,
    useRemoveListFromRoutePlanner,
  };
};

export default useRoutePlannerAction;
